import { Button } from '@cluey/cluey-components';
import classNames from 'classnames';
import { isEmpty, isUndefined } from 'lodash';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { api } from '../../../../../api';
import { Enrolment } from '../../../../../api/types/enrolment';
import { Student } from '../../../../../api/types/student';
import CheckTickIcon from '../../../../../assets/images/icon-filled-success-tick-white.svg';
import MenuMeatballsIcon from '../../../../../assets/images/icon-outline-menu-meatball-white.svg';
import { InlineLoader } from '../../../../../common/InlineLoader';
import DropdownMenu from '../../../../../components/DropdownMenu/DropdownMenu';
import { SkeletonLoader } from '../../../../../components/SkeletonLoader';
import useAccountPricingPlanQuery from '../../../../../hooks/queries/useAccountPricingPlanQuery';
import { useAvailableSubjectsQuery } from '../../../../../hooks/queries/useEnrolmentQuery';
import { COUNTRY, ENROLMENT_STATUS } from '../../../../../types/hubEnums';
import { BillingInformationType } from '../../../../../types/hubPropTypes';
import { formatError } from '../../../../../util/errors';
import {
  PATH_HUB_CR_ADD_ENROLMENT,
  PATH_HUB_INVOICE_HISTORY,
  PATH_HUB_PLAN_DETAILS,
} from '../../../../../util/pagePath';
import { Checkbox } from '../../../../../v2/components/ui/checkbox';
import { SUBSCRIPTION_LOCAL_STORAGE_KEYS, SubscriptionLocalStorage } from '../../SubscriptionLocalStorage';
import useSubscriptionContext, { CONFIRMATION_TYPE, SUBSCRIPTION_TYPE } from '../../useSubscriptionContext';
import { BANNER_TYPE, Banners } from './Banners';
import { EnrolmentDetails } from './EnrolmentDetails';
import SavedPaymentDetails from './SavedPaymentDetails';

interface StudentDetailsProps {
  student: Student;
  enrolments: Enrolment[];
  setSelectedEnrolment: (enrolment: Enrolment) => void;
}

interface PaymentDetailsSectionProps {
  isLoading: boolean;
  billingInformation: BillingInformationType;
  onSuccess: () => void;
  recentlySaved: boolean;
}

const PaymentDetailsSection = ({
  isLoading,
  billingInformation,
  onSuccess,
  recentlySaved,
}: PaymentDetailsSectionProps) => {
  if (recentlySaved) {
    return (
      <div className="rounded-2xl bg-white p-5 !text-grey-6">
        <p className="text-sm font-medium">Payment details updated.</p>
      </div>
    );
  }
  if (isLoading) {
    return <SkeletonLoader loading={true} className="h-12 w-full" />;
  }
  return (
    <div className="rounded-2xl bg-white p-5 !text-grey-6">
      <SavedPaymentDetails billingInformation={billingInformation} onSuccess={onSuccess} />
    </div>
  );
};

export const StudentDetails = ({ student, enrolments, setSelectedEnrolment }: StudentDetailsProps) => {
  const history = useHistory();

  const {
    clueyPlus: {
      isLoading: isLoadingClueyPlus,
      isESSLRecordsLoading,
      hasMultipleActiveSubscriptions,
      getSubscription,
      refetchESSLRecords,
      showConfirmationDialog,
      toast: { setShow: setToastShow, setMessage: setToastMessage },
    },
    banner: { setShow: setBannerShow, setMessage: setBannerMessage },
    billing: {
      info: { billingInformation, isLoading: isLoadingBillingInformation },
    },
    student: { selectedStudentEnrolments },
  } = useSubscriptionContext();

  const { data: availableSubjects } = useAvailableSubjectsQuery({
    year: student?.yearLevel ? student?.yearLevel?.split(' ')[1] : undefined,
    country: student
      ? student.studentCountry === COUNTRY.AUSTRALIA.NAME
        ? COUNTRY.AUSTRALIA.COUNTRY_CODE.toLowerCase()
        : COUNTRY.NEW_ZEALAND.COUNTRY_CODE.toLowerCase()
      : undefined,
  });

  const {
    data: accountDetails,
    isLoading: isLoadingAccountDetails,
    error: accountDetailsError,
  } = api.account.details.useQuery();

  const {
    mutate: trialConversion,
    isLoading: isLoadingTrialConversion,
    error: trialConversionError,
  } = api.clueyPlus.trialConversion.useMutation();

  const {
    mutate: resubscribeToClueyPlus,
    isLoading: isLoadingResubscribeToClueyPlus,
    error: resubscribeToClueyPlusError,
  } = api.clueyPlus.resubscribeToClueyPlus.useMutation();

  const {
    data: accountPricingPlan,
    isLoading: isLoadingAccountPricingPlan,
    error: accountPricingPlanError,
  } = useAccountPricingPlanQuery();

  const { subscriptionType, subscriptionStatus, subscriptionPrice, subscriptionId, subscriptionPaidUntilDate } =
    getSubscription() ?? {};

  const clueyPlusSubscription = useMemo(() => {
    if (!isLoadingClueyPlus && subscriptionType === SUBSCRIPTION_TYPE.TRIAL && hasMultipleActiveSubscriptions) {
      return getSubscription(SUBSCRIPTION_TYPE.CLUEY_PLUS);
    }
  }, [getSubscription, hasMultipleActiveSubscriptions, isLoadingClueyPlus, subscriptionType]);

  const [subscriptionTermsAndConditions, setSubscriptionTermsAndConditions] = useState(false);
  const [subscriptionPaymentAgreement, setSubscriptionPaymentAgreement] = useState(false);
  const [missingPaymentDetailsError, setMissingPaymentDetailsError] = useState<string | null>(null);
  const [paymentDetailsUpdated, setPaymentDetailsUpdated] = useState(false);

  const withActiveEnrolmentDetails = useCallback(
    (showCancelEnrolment: boolean = true, showCancelOptionForLastEnrolment: boolean = false) => {
      if (!student) {
        return null;
      }

      const unenrolledSubjects = (availableSubjects ?? [])
        .filter((subject: string) => {
          return !(enrolments ?? []).some((e) => {
            return (
              e.subject.includes(subject) ||
              (subject.includes(e.subject) &&
                !(e.enrolmentStatus === ENROLMENT_STATUS.CANCEL_REQUESTED && isEmpty(e.sessions)))
            );
          });
        })
        .sort((a, b) => a.localeCompare(b));

      const hasMultipleActiveEnrolments =
        (enrolments ?? []).filter((e) => {
          return ![ENROLMENT_STATUS.CANCEL_REQUESTED].includes(e.enrolmentStatus);
        }).length > 1;

      const activeEnrolments = enrolments
        .filter((enrolment) => !unenrolledSubjects.includes(enrolment.subject))
        .filter(
          (enrolment) =>
            !(enrolment.enrolmentStatus === ENROLMENT_STATUS.CANCEL_REQUESTED && isEmpty(enrolment.sessions))
        )
        .sort((a, b) => a.subject.localeCompare(b.subject));

      return (
        <div className="flex flex-col gap-y-6">
          <div className="flex flex-col gap-y-0">
            <p className="arial text-sm font-semibold text-blood-orange-4">Accelerate {student.firstName}'s learning</p>
            <h1 className="merriweather text-2xl font-bold">Tutoring for {student.firstName}</h1>
          </div>
          <div className="text-green-1">
            <p>Personalised one-to-one sessions designed to accelerate learning outcomes.</p>
          </div>
          <div className="flex flex-row justify-between">
            <div className="flex flex-row items-end gap-x-1">
              <span className="text-2xl font-semibold text-white">
                ${accountPricingPlan?.currentPricingPlan?.privatePriceTier?.costPerSession}
              </span>
              <span className="text-sm font-medium text-[#AFE680]">/session</span>
            </div>
            <div className="flex flex-row items-center gap-x-2">
              <span className="rounded-full bg-green-3 px-2 py-1 text-center text-xs font-semibold text-grey-6">
                {accountPricingPlan?.currentPricingPlan?.pricingPlanDisplayName} plan
              </span>
              <DropdownMenu
                items={[{ label: 'See plan details', onClick: () => history.push(PATH_HUB_PLAN_DETAILS) }]}
                icon={<img src={MenuMeatballsIcon} alt="menu icon" />}
                showIcon={false}
                buttonClassName="border-none !px-1"
                itemClassName="!py-3 !px-5 rounded-lg"
              />
            </div>
          </div>
          {activeEnrolments.length > 0 && (
            <div className="flex flex-col gap-y-3 rounded-2xl border border-white/20 bg-white/10 p-5">
              <h2 className="text-lg font-semibold text-[#AFE680]">Active enrolments</h2>
              {activeEnrolments.map((enrolment) => (
                <EnrolmentDetails
                  key={`${enrolment.enrolmentId}-enrolment-details`}
                  enrolment={enrolment}
                  setSelectedEnrolment={setSelectedEnrolment}
                  showCancelEnrolment={
                    showCancelEnrolment
                      ? showCancelOptionForLastEnrolment
                        ? !hasMultipleActiveEnrolments
                        : true
                      : false
                  }
                />
              ))}
            </div>
          )}
          {unenrolledSubjects.length > 0 && (
            <div className="flex flex-col gap-y-4">
              {unenrolledSubjects.map((subject) => (
                <div
                  key={subject}
                  className="flex flex-row justify-between gap-y-3 rounded-2xl border border-white/20 bg-white/10 p-5"
                >
                  <div className="flex flex-col">
                    <h2 className="text-base font-medium text-white">{subject}</h2>
                    <p className="text-sm font-medium text-grey-2">
                      <i>Not enrolled</i>
                    </p>
                  </div>
                  <div className="py-1.5">
                    <Button
                      size="lg"
                      className="!border-none !bg-blood-orange-4 !px-4 !text-sm text-white"
                      onClick={() => {
                        history.push(PATH_HUB_CR_ADD_ENROLMENT, {
                          firstName: student.firstName,
                          lastName: student.lastName,
                          yearLevel: student.yearLevel,
                          location: student.location,
                          studentId: student.studentId,
                          subject,
                        });
                      }}
                    >
                      Enrol
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    },
    [accountPricingPlan, availableSubjects, enrolments, history, setSelectedEnrolment, student]
  );

  const clueyPlusOnlyCustomerDetails = useCallback(() => {
    return (
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-0">
          <p className="arial text-sm font-semibold text-blood-orange-4">Continue {student.firstName}'s learning</p>
          <h1 className="merriweather text-2xl font-bold">Add 1-on-1 Tutoring</h1>
        </div>
        <div className="text-green-1">
          {isUndefined(availableSubjects) ? (
            <p>No available subjects available (No year level set)</p>
          ) : (
            <p>Choose your subjects and grow your skills with expert tutors.</p>
          )}
        </div>
        {availableSubjects && availableSubjects.length > 0 && (
          <div className="flex flex-col gap-y-4 rounded-2xl border border-white/20 bg-white/10">
            {/* TODO: Hard coded session price */}
            <div className="flex flex-row items-center gap-x-1 px-5 py-5">
              <p className="font-medium text-[#AFE680]">Session price from</p>
              <span className="text-2xl font-semibold text-white">
                ${accountPricingPlan?.currentPricingPlan?.privatePriceTier?.costPerSession}
              </span>
            </div>
            {availableSubjects.map((subject) => (
              <div
                key={subject}
                className="mx-5 flex flex-row justify-between gap-y-3 border-b border-white/20 pb-3 last:border-b-0"
              >
                <div className="flex flex-col">
                  <h2 className="text-base font-medium text-white">{subject}</h2>
                  <p className="text-sm font-medium text-[#AFE680]">Available now</p>
                </div>
                <div className="py-1.5">
                  <Button
                    size="lg"
                    className="!border-none !bg-blood-orange-4 !px-4 !text-sm !font-medium text-white"
                    onClick={() => {
                      history.push(PATH_HUB_CR_ADD_ENROLMENT, {
                        firstName: student.firstName,
                        lastName: student.lastName,
                        yearLevel: student.yearLevel,
                        location: student.location,
                        studentId: student.studentId,
                        subject,
                      });
                    }}
                  >
                    Enrol
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }, [availableSubjects, history, student, accountPricingPlan?.currentPricingPlan?.privatePriceTier?.costPerSession]);

  const handlePaymentSuccess = useCallback(() => {
    setMissingPaymentDetailsError(null);
    setPaymentDetailsUpdated(true);
  }, [setMissingPaymentDetailsError, setPaymentDetailsUpdated]);

  const clueyPlusSubscribe = useCallback(() => {
    const isCancelledClueyPlus = subscriptionStatus === 'Cancelled';
    const subscriptionDescription = isCancelledClueyPlus ? 'Resubscribe' : 'Subscribe';
    const isNZ = accountDetails?.country === 'NZ';
    const effectiveFromDate = moment(subscriptionPaidUntilDate).isAfter(moment(), 'day')
      ? moment(subscriptionPaidUntilDate).add(1, 'day').format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');

    return (
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-0">
          <p className="arial text-sm font-semibold text-blood-orange-4">Continue {student.firstName}'s learning</p>
          <h1 className="merriweather text-2xl font-bold">
            {subscriptionType === SUBSCRIPTION_TYPE.TRIAL && clueyPlusSubscription
              ? 'Cluey+ subscription'
              : `${subscriptionDescription} to Cluey+`}
          </h1>
        </div>
        {subscriptionType === SUBSCRIPTION_TYPE.TRIAL && clueyPlusSubscription ? (
          <>
            <div className="flex flex-row justify-between">
              <div className="flex flex-row items-end gap-x-1">
                <span className="text-2xl font-semibold text-white">${clueyPlusSubscription.subscriptionPrice}</span>
                <span className="text-sm font-medium text-[#AFE680]">/month</span>
              </div>
            </div>
            <div className="flex flex-row items-start gap-x-3 rounded-2xl bg-white p-5">
              <img src={CheckTickIcon} alt="check tick icon" className="fill-green-3" width={20} height={20} />
              <div className="flex flex-col gap-y-3 text-start text-grey-6">
                <h2 className="merriweather text-lg font-bold">You are subscribed to Cluey+</h2>
                <p className="text-base">
                  Your subscription will start on{' '}
                  <i>{moment(clueyPlusSubscription.subscriptionStartDate).format('DD MMM')}</i> after your free trial
                  ends
                </p>
              </div>
            </div>
            <Button
              size="lg"
              appearance="reverse"
              type="submit"
              className="flex !h-10 w-full max-w-sm flex-row items-center justify-center !border !border-white !bg-transparent !text-base !font-medium text-white"
              onClick={() => {
                history.push(PATH_HUB_INVOICE_HISTORY);
              }}
            >
              Update payment details
            </Button>
          </>
        ) : (
          <>
            <div>
              <p className="font-normal text-green-1">
                {subscriptionDescription} to Cluey+ to gain unlimited access to learning content after your trial.
              </p>
            </div>
            <div className="flex flex-row justify-between">
              <div className="flex flex-row items-end gap-x-1">
                <span className="text-2xl font-semibold text-white">
                  {subscriptionType === SUBSCRIPTION_TYPE.TRIAL
                    ? '$30'
                    : subscriptionPrice > 0
                      ? `$${subscriptionPrice}`
                      : '$30'}
                </span>
                <span className="text-sm font-medium text-[#AFE680]">/month</span>
              </div>
            </div>

            <PaymentDetailsSection
              isLoading={isLoadingBillingInformation}
              billingInformation={billingInformation}
              onSuccess={handlePaymentSuccess}
              recentlySaved={paymentDetailsUpdated}
            />

            <div className="flex flex-col items-start gap-y-3">
              <div className="flex flex-row items-center gap-x-2">
                <Checkbox
                  id="trial-customer-terms-and-conditions"
                  checked={subscriptionTermsAndConditions}
                  onCheckedChange={() => setSubscriptionTermsAndConditions(!subscriptionTermsAndConditions)}
                  className="bg-white"
                />
                <label htmlFor="trial-customer-terms-and-conditions" className="cursor-pointer !text-sm font-medium">
                  I have read and agree to Cluey&apos;s{' '}
                  <a
                    className="underline"
                    href={
                      isNZ
                        ? 'https://clueylearning.co.nz/cluey-plus-agreement/'
                        : 'https://clueylearning.com.au/cluey-plus-agreement/'
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms and Conditions
                  </a>
                </label>
              </div>
              <div className="flex flex-row items-center gap-x-2">
                <Checkbox
                  id="subscription-payment-agreement"
                  checked={subscriptionPaymentAgreement}
                  onCheckedChange={() => setSubscriptionPaymentAgreement(!subscriptionPaymentAgreement)}
                  className="bg-white"
                />
                <label htmlFor="subscription-payment-agreement" className="cursor-pointer !text-sm font-medium">
                  I agree to ongoing monthly payments for my Cluey+ subscription (cancel anytime)
                </label>
              </div>
            </div>
            <div className="flex w-full flex-col justify-center gap-y-2">
              {trialConversionError && <p className="text-red-4">{formatError(trialConversionError)}</p>}
              {resubscribeToClueyPlusError && <p className="text-red-4">{formatError(resubscribeToClueyPlusError)}</p>}
              {missingPaymentDetailsError && <p className="text-red-4">{missingPaymentDetailsError}</p>}
              <Button
                size="lg"
                type="submit"
                disabled={!subscriptionPaymentAgreement || !subscriptionTermsAndConditions}
                className={classNames(
                  'flex !h-9 w-full flex-row items-center justify-center !border-blood-orange-4 !bg-blood-orange-4 !text-base !font-medium !text-white',
                  (!subscriptionPaymentAgreement || !subscriptionTermsAndConditions) && 'cursor-not-allowed'
                )}
                onClick={() => {
                  if (
                    (!billingInformation?.creditCardType || !billingInformation?.endingIn) &&
                    !paymentDetailsUpdated
                  ) {
                    setMissingPaymentDetailsError('Please update your payment details to continue');
                    return;
                  }
                  if (subscriptionType === SUBSCRIPTION_TYPE.TRIAL) {
                    trialConversion(
                      {
                        accountId: accountDetails?.customerSfid,
                        customerSubscriptionId: subscriptionId ?? '',
                        countryCode: accountDetails?.country,
                        effectiveFromDate,
                      },
                      {
                        onSuccess: () => {
                          showConfirmationDialog(CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CREATED);
                          setBannerShow(true);
                          setBannerMessage(<Banners type={BANNER_TYPE.SUBSCRIPTION_CREATED} />);
                          SubscriptionLocalStorage.set(
                            SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_SUBS_BEING_CREATED,
                            student.studentId
                          );
                        },
                        onError: (error) => {
                          setToastShow(true);
                          setToastMessage(formatError(error));
                          SubscriptionLocalStorage.remove(
                            SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_SUBS_BEING_CREATED,
                            student.studentId
                          );
                        },
                      }
                    );
                  } else if (isCancelledClueyPlus) {
                    resubscribeToClueyPlus(
                      {
                        accountId: accountDetails?.customerSfid,
                        countryCode: accountDetails?.country,
                        students: [
                          {
                            sfid: student.studentId,
                            firstName: student.firstName,
                            lastName: student.lastName,
                            yearLevel: student.yearLevel,
                            state: accountDetails?.country === 'NZ' ? '' : student.location,
                            postcode: '',
                          },
                        ],
                      },
                      {
                        onSuccess: () => {
                          refetchESSLRecords();
                          showConfirmationDialog(CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CREATED);
                          SubscriptionLocalStorage.set(
                            SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_SUBS_BEING_CREATED,
                            student.studentId
                          );
                        },
                        onError: (error) => {
                          setToastShow(true);
                          setToastMessage(formatError(error));
                          SubscriptionLocalStorage.remove(
                            SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_SUBS_BEING_CREATED,
                            student.studentId
                          );
                        },
                      }
                    );
                  }
                }}
              >
                {isLoadingTrialConversion || isLoadingResubscribeToClueyPlus || isLoadingBillingInformation ? (
                  <InlineLoader />
                ) : (
                  `${subscriptionDescription} to Cluey+`
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }, [
    subscriptionStatus,
    accountDetails?.country,
    accountDetails?.customerSfid,
    subscriptionPaidUntilDate,
    student.firstName,
    student.studentId,
    student.lastName,
    student.yearLevel,
    student.location,
    subscriptionType,
    clueyPlusSubscription,
    subscriptionPrice,
    isLoadingBillingInformation,
    billingInformation,
    handlePaymentSuccess,
    paymentDetailsUpdated,
    subscriptionTermsAndConditions,
    subscriptionPaymentAgreement,
    trialConversionError,
    resubscribeToClueyPlusError,
    missingPaymentDetailsError,
    isLoadingTrialConversion,
    isLoadingResubscribeToClueyPlus,
    history,
    trialConversion,
    subscriptionId,
    showConfirmationDialog,
    setBannerShow,
    setBannerMessage,
    setToastShow,
    setToastMessage,
    resubscribeToClueyPlus,
    refetchESSLRecords,
  ]);

  const clueyPlusSubscriptionEnded = useCallback(() => {
    return (
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-0">
          <p className="arial text-sm font-semibold text-blood-orange-4">Continue {student.firstName}'s learning</p>
          <h1 className="merriweather text-2xl font-bold">Cluey+ subscription</h1>
        </div>
        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-end gap-x-1">
            <span className="text-2xl font-semibold text-white">${subscriptionPrice}</span>
            <span className="text-sm font-medium text-[#AFE680]">/month</span>
          </div>
        </div>
        <div className="flex flex-row items-start gap-x-3 rounded-2xl bg-white p-5">
          <img src={CheckTickIcon} alt="check tick icon" className="fill-green-3" width={20} height={20} />
          <div className="flex flex-col gap-y-3 text-start text-grey-6">
            <h2 className="merriweather text-lg font-bold">You have ended your Cluey+ subscription</h2>
            <p className="text-base">
              You still have access to your Cluey+ until {moment(subscriptionPaidUntilDate).format('DD MMM')}. You can
              resubscribe after this date.
            </p>
          </div>
        </div>
      </div>
    );
  }, [subscriptionPaidUntilDate, subscriptionPrice, student.firstName]);

  const isSubscriptionPastPUD = useMemo(() => {
    return subscriptionPaidUntilDate && moment(subscriptionPaidUntilDate).isBefore(moment(), 'day');
  }, [subscriptionPaidUntilDate]);

  const isLoading = isLoadingAccountPricingPlan || isLoadingAccountDetails || isESSLRecordsLoading;
  const error =
    accountPricingPlanError || accountDetailsError
      ? formatError(accountPricingPlanError || accountDetailsError)
      : undefined;

  if (error) {
    return <div className="flex flex-col gap-y-4 font-semibold text-red-4">{error}</div>;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-y-4">
        <SkeletonLoader loading={isLoading} className="h-4 w-32" />
        <SkeletonLoader loading={isLoading} className="mt-6 h-12 w-full" />
        <div className="flex flex-row items-center justify-between gap-x-2">
          <SkeletonLoader loading={isLoading} className="h-8 w-24" />
          <SkeletonLoader loading={isLoading} className="h-8 w-24" />
        </div>
        <SkeletonLoader loading={isLoading} className="mt-4 h-32 w-full" />
      </div>
    );
  }

  switch (subscriptionType) {
    default:
    case SUBSCRIPTION_TYPE.BUNDLED:
      if (subscriptionStatus === 'Cancelled') {
        return clueyPlusSubscribe();
      }
      return withActiveEnrolmentDetails(true, true);
    case SUBSCRIPTION_TYPE.CLUEY_PLUS:
      if (subscriptionStatus === 'Cancelled') {
        if (!isSubscriptionPastPUD) {
          return clueyPlusSubscriptionEnded();
        }
        return clueyPlusSubscribe();
      }
      // Separate billing enrollment details
      if (selectedStudentEnrolments.length > 0) {
        return withActiveEnrolmentDetails(false);
      }
      return clueyPlusOnlyCustomerDetails();
    case SUBSCRIPTION_TYPE.TRIAL:
      return clueyPlusSubscribe();
  }
};
